import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  dataExchangeApi,
  dataMappingApi,
  employeeIdConfigApi,
  permissionsApi,
} from "../adminToolsApi";

const useKeysMutation = (fn, keys) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () =>
      keys.forEach((queryKey) => qc.invalidateQueries({ queryKey })),
  });
};

/* ---------- Permissions ---------- */
export const usePermissionModules = () =>
  useQuery({
    queryKey: ["permissions", "modules"],
    queryFn: permissionsApi.getModules,
    staleTime: 30 * 60 * 1000,
  });
// Keyed by user (and token) so a new login never reuses another session's permissions
export const useMyPermissions = ({ userId, token, enabled = true } = {}) =>
  useQuery({
    queryKey: [
      "permissions",
      "me",
      userId || null,
      token ? token.slice(-16) : null,
    ],
    queryFn: permissionsApi.getMine,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled,
  });
export const useRolePermissions = () =>
  useQuery({
    queryKey: ["permissions", "roles"],
    queryFn: permissionsApi.getRoles,
  });
export const useSaveRolePermissions = () =>
  useKeysMutation(
    ({ roleId, permissions }) => permissionsApi.saveRole(roleId, permissions),
    [["permissions"]],
  );
export const useResetRolePermissions = () =>
  useKeysMutation(
    (roleId) => permissionsApi.resetRole(roleId),
    [["permissions"]],
  );

/* ---------- Import / export ---------- */
export const useDatasets = () =>
  useQuery({
    queryKey: ["dataExchange", "datasets"],
    queryFn: dataExchangeApi.getDatasets,
    staleTime: 30 * 60 * 1000,
  });
export const useDataTemplates = (params, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["dataExchange", "templates", params],
    queryFn: () => dataExchangeApi.getTemplates(params),
    enabled,
  });
export const useCreateDataTemplate = () =>
  useKeysMutation(
    (body) => dataExchangeApi.createTemplate(body),
    [["dataExchange", "templates"]],
  );
export const useUpdateDataTemplate = () =>
  useKeysMutation(
    ({ id, ...body }) => dataExchangeApi.updateTemplate(id, body),
    [["dataExchange", "templates"]],
  );
export const useDeleteDataTemplate = () =>
  useKeysMutation(
    (id) => dataExchangeApi.deleteTemplate(id),
    [["dataExchange", "templates"]],
  );
export const useDataImports = (params) =>
  useQuery({
    queryKey: ["dataExchange", "imports", params],
    queryFn: () => dataExchangeApi.getImports(params),
    placeholderData: keepPreviousData,
  });
/** Dry run or real import; a real one refreshes the history and every list it may change. */
export const useRunDataImport = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => dataExchangeApi.importFile(args),
    onSuccess: (_res, { dryRun }) => {
      if (dryRun) return;
      [
        ["dataExchange", "imports"],
        ["importLogs"],
        ["employees"],
        ["assets"],
        ["leaveAllocations"],
        ["users"],
      ].forEach((queryKey) => qc.invalidateQueries({ queryKey }));
    },
  });
};

/* ---------- Data mapping ---------- */
export const useMappingModules = () =>
  useQuery({
    queryKey: ["dataMapping", "modules"],
    queryFn: dataMappingApi.getModules,
    staleTime: 30 * 60 * 1000,
  });
export const useMappings = (module = "employee") =>
  useQuery({
    queryKey: ["dataMapping", "list", module],
    queryFn: () => dataMappingApi.getList(module),
  });
export const useEffectiveMapping = (companyId, { enabled = true } = {}) =>
  useQuery({
    queryKey: ["dataMapping", "effective", companyId || "mine"],
    queryFn: () => dataMappingApi.getEffective(companyId),
    enabled,
    retry: false,
  });
const MAPPING_KEYS = [["dataMapping"]];
export const useCreateMapping = () =>
  useKeysMutation((body) => dataMappingApi.create(body), MAPPING_KEYS);
export const useUpdateMapping = () =>
  useKeysMutation(
    ({ id, ...body }) => dataMappingApi.update(id, body),
    MAPPING_KEYS,
  );
export const useDeleteMapping = () =>
  useKeysMutation((id) => dataMappingApi.remove(id), MAPPING_KEYS);

/* ---------- Employee ID format ---------- */
export const useEmployeeIdConfig = (companyId) =>
  useQuery({
    queryKey: ["employeeIdConfig", companyId],
    queryFn: async () => {
      try {
        return await employeeIdConfigApi.get(companyId);
      } catch (err) {
        if (err?.response?.status === 404) return null; // not set up yet
        throw err;
      }
    },
    enabled: !!companyId,
    retry: false,
  });
export const useEmployeeIdPreview = (
  companyId,
  { clientId, hiringDate } = {},
  { enabled = true } = {},
) =>
  useQuery({
    queryKey: [
      "employeeIdConfig",
      companyId,
      "preview",
      clientId || null,
      hiringDate || null,
    ],
    queryFn: () =>
      employeeIdConfigApi.preview(companyId, { clientId, hiringDate }),
    enabled: !!companyId && enabled,
    retry: false,
  });
export const useClientIdFormats = () =>
  useQuery({
    queryKey: ["employeeIdConfig", "clients"],
    queryFn: employeeIdConfigApi.getClients,
  });
export const useSaveClientIdFormat = () =>
  useKeysMutation(
    ({ clientId, ...body }) => employeeIdConfigApi.saveClient(clientId, body),
    [["employeeIdConfig"]],
  );
export const useRemoveClientIdFormat = () =>
  useKeysMutation(
    (clientId) => employeeIdConfigApi.removeClient(clientId),
    [["employeeIdConfig"]],
  );
export const useSaveEmployeeIdConfig = () =>
  useKeysMutation(
    ({ companyId, ...body }) => employeeIdConfigApi.save(companyId, body),
    [["employeeIdConfig"]],
  );
